import React, { useEffect, useState } from "react";
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme } from
"@mui/material";
import { LoadingButton } from "@mui/lab";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import CloseIcon from "@mui/icons-material/Close";
import { useTranslation } from "react-i18next";
import { CustomDomainCnameRecord, CustomDomainSetupResponse } from "crqlar-react-common/dist/models/newsletter/customDomain.model";
import { CustomDomainService } from "crqlar-react-common/dist/services/newsletter/customDomain.service";
import CopyAdornment from "crqlar-react-common/dist/components/CopyAdornment";
import { TransactionalMailTemplateService } from "crqlar-react-common/dist/services/newsletter/transactionalMailTemplate.service";
import { useReadOnly } from "crqlar-react-common/dist/utils/hooks/users/useReadOnly";

const getRelativeHost = (fullHost: string, baseDomain: string): string => {
  const suffix = "." + baseDomain;
  return fullHost.endsWith(suffix) ? fullHost.slice(0, -suffix.length) : fullHost;
};

type SetupState = "idle" | "saving" | "saved" | "verifying" | "verified";

interface AlertInfo {
  severity: "error" | "warning" | "success" | "info";
  text: string;
}

interface ConnectCustomDomainDialogProps {
  open: boolean;
  onClose: () => void;
  existingDomain?: CustomDomainSetupResponse | null;
  onDomainSaved?: () => void;
}

const ConnectCustomDomainDialog: React.FC<ConnectCustomDomainDialogProps> = ({ open, onClose, existingDomain, onDomainSaved }) => {
  const { t } = useTranslation();
  // Read-only users may view the domain setup/DNS records, but not connect or edit a domain.
  const readOnly = useReadOnly();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [subdomain, setSubdomain] = useState("");
  const [domain, setDomain] = useState("");
  const [setupState, setSetupState] = useState<SetupState>("idle");
  const [domainId, setDomainId] = useState<number | null>(null);
  const [cnameRecords, setCnameRecords] = useState<CustomDomainCnameRecord[]>([]);
  const [alertInfo, setAlertInfo] = useState<AlertInfo | null>(null);
  const [updateTemplatesConfirmOpen, setUpdateTemplatesConfirmOpen] = useState(false);
  const [updatingTemplates, setUpdatingTemplates] = useState(false);
  const [newDomainSuffix, setNewDomainSuffix] = useState("");

  useEffect(() => {
    if (!open) return;

    if (existingDomain) {
      setSubdomain(existingDomain.subdomain ?? "");
      setDomain(existingDomain.domain ?? "");
      setCnameRecords(existingDomain.cnameRecords ?? []);
      setDomainId(existingDomain.id ?? null);

      if (existingDomain.valid) {
        setSetupState("verified");
        setAlertInfo({
          severity: "success",
          text: t("Your domain {{subdomain}}.{{domain}} is authenticated and active.", {
            subdomain: existingDomain.subdomain,
            domain: existingDomain.domain
          })
        });
      } else {
        setSetupState("saved");
        setAlertInfo({
          severity: "info",
          text: t("Domain is configured but not yet verified. Add the DNS records below to your domain provider, then click Verify.")
        });
      }
    } else {
      setSubdomain("");
      setDomain("");
      setCnameRecords([]);
      setDomainId(null);
      setSetupState("idle");
      setAlertInfo(null);
    }
  }, [open, existingDomain]);

  const handleClose = () => {
    setAlertInfo(null);
    setUpdateTemplatesConfirmOpen(false);
    setNewDomainSuffix("");
    onClose();
  };

  const handleSave = async () => {
    if (!subdomain.trim() || !domain.trim()) return;
    setSetupState("saving");
    setAlertInfo(null);
    try {
      const response = await CustomDomainService.setup({
        subdomain: subdomain.trim(),
        domain: domain.trim()
      });
      setCnameRecords(response.cnameRecords ?? []);
      setDomainId(response.id ?? null);
      setSetupState("saved");
      onDomainSaved?.();
      setNewDomainSuffix(`@${subdomain.trim()}.${domain.trim()}`);
      setUpdateTemplatesConfirmOpen(true);
    } catch (err: any) {
      setSetupState("idle");
      // Translating the response message so the UX is better for users
      // SendGrid subuser creation failed (HTTP 400): username exists is the key in the en.json, de.json, it.json files
      const msg = t(err?.response?.data?.message) ?? t("Domain setup failed. Please try again.");
      setAlertInfo({ severity: "error", text: msg });
    }
  };

  const handleVerify = async () => {
    if (!domainId) return;
    setSetupState("verifying");
    setAlertInfo(null);
    try {
      const response = await CustomDomainService.validate(domainId);
      if (response.valid) {
        setSetupState("verified");
        setAlertInfo({ severity: "success", text: response.message ?? t("Domain authenticated successfully") });
        onDomainSaved?.();
      } else {
        setSetupState("saved");
        const msg = response.message ?? t("Verification failed. Please check your DNS records and try again.");
        setAlertInfo({ severity: "warning", text: msg });
      }
    } catch (err: any) {
      setSetupState("saved");
      const msg = err?.response?.data?.message ?? t("Verification failed. Please check your DNS records and try again.");
      setAlertInfo({ severity: "error", text: msg });
    }
  };

  const handleUpdateTemplates = async () => {
    setUpdatingTemplates(true);
    try {
      const templates = await TransactionalMailTemplateService.getAll();
      const toUpdate = templates.filter((tmpl) => !!tmpl.fromEmail);
      await Promise.all(
        toUpdate.map((tmpl) => {
          const prefix = tmpl.fromEmail!.split('@')[0];
          return TransactionalMailTemplateService.update({
            ...tmpl,
            fromEmail: `${prefix}${newDomainSuffix}`
          });
        })
      );
    } finally {
      setUpdatingTemplates(false);
      setUpdateTemplatesConfirmOpen(false);
    }
  };

  const handleEdit = () => {
    setSetupState("idle");
    setAlertInfo(null);
  };

  const isFieldsDisabled = setupState !== "idle";
  const showTable = setupState === "saved" || setupState === "verifying" || setupState === "verified";
  const showEdit = setupState === "saved" || setupState === "verifying" || setupState === "verified";

  const renderActionButton = () => {
    if (setupState === "verified") {
      return (
        <LoadingButton
          variant="contained"
          color="success"
          startIcon={<CheckCircleIcon />}
          size="large"
          sx={{ whiteSpace: "nowrap", minHeight: 56, px: 3 }}
          disabled>

                    {t("Success")}
                </LoadingButton>);

    }

    if (setupState === "saved" || setupState === "verifying") {
      return null;
    }

    return (
      <LoadingButton
        variant="contained"
        loading={setupState === "saving"}
        loadingPosition="start"
        startIcon={<span />}
        size="large"
        onClick={handleSave}
        disabled={!subdomain.trim() || !domain.trim() || readOnly}
        sx={{ whiteSpace: "nowrap", minHeight: 56, px: 3 }}>

                {setupState === "saving" ? t("Saving...") : t("Save")}
            </LoadingButton>);

  };

  return (
    <>
        <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="lg"
        fullWidth
        fullScreen={isMobile}
        PaperProps={{
          sx: { minHeight: showTable ? 480 : 240 }
        }}>

            <DialogTitle sx={{ pb: 1 }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Typography variant="h6" fontWeight={600}>
                        {t("Set up your custom mailing domain")}
                    </Typography>
                    <IconButton onClick={handleClose} size="small">
                        <CloseIcon />
                    </IconButton>
                </Stack>
            </DialogTitle>

            <DialogContent sx={{ pt: 2 }}>
                <Stack spacing={3}>
                    <Stack
              direction={isMobile ? "column" : "row"}
              alignItems={isMobile ? "stretch" : "flex-end"}
              spacing={isMobile ? 2 : 1.5}>

                        <Stack spacing={0.75} flex={1}>
                            <Stack direction="row" alignItems="center" spacing={0.5}>
                                <Typography variant="body1" fontWeight={600}>
                                    {t("subdomain")}
                                </Typography>
                                <Tooltip title={t("The subdomain prefix used for your custom mailing domain (e.g. 'mail' in mail.example.com)")}>
                                    <InfoOutlinedIcon sx={{ fontSize: 18, color: "primary.main", cursor: "pointer" }} />
                                </Tooltip>
                            </Stack>
                            <TextField
                  value={subdomain}
                  onChange={(e) => setSubdomain(e.target.value)}
                  disabled={isFieldsDisabled}
                  fullWidth />

                        </Stack>

                        {!isMobile &&
              <Typography variant="h5" pb={1.5} fontWeight={600} color="text.secondary">
                                .
                            </Typography>
              }

                        <Stack spacing={0.75} flex={2}>
                            <Stack direction="row" alignItems="center" spacing={0.5}>
                                <Typography variant="body1" fontWeight={600}>
                                    {t("domain")}
                                </Typography>
                                <Tooltip title={t("Your base domain (e.g. 'example.com')")}>
                                    <InfoOutlinedIcon sx={{ fontSize: 18, color: "primary.main", cursor: "pointer" }} />
                                </Tooltip>
                            </Stack>
                            <TextField
                  value={domain}
                  onChange={(e) => setDomain(e.target.value)}
                  disabled={isFieldsDisabled}
                  fullWidth />

                        </Stack>

                        <Stack sx={{ alignSelf: isMobile ? "stretch" : "flex-end" }}>
                            {renderActionButton()}
                        </Stack>
                    </Stack>

                    {alertInfo &&
            <Alert
              severity={alertInfo.severity}
              onClose={() => setAlertInfo(null)}
              sx={{ fontSize: "0.95rem" }}>

                            {alertInfo.text}
                        </Alert>
            }

                    {showTable &&
            <Stack spacing={2}>
                            <Table sx={{ border: "1px solid", borderColor: "divider", borderRadius: 1 }}>
                                <TableHead>
                                    <TableRow>
                                        <TableCell sx={{ fontWeight: 700, fontSize: "1rem", width: 80 }}>{t("Type")}</TableCell>
                                        <TableCell sx={{ fontWeight: 700, fontSize: "1rem" }}>
                                            <Stack direction="row" alignItems="center" spacing={0.75}>
                                                {t("Hostname")}
                                                <Tooltip title={t("Enter only the highlighted prefix into your registrar's hostname field — it appends your domain automatically.")}>
                                                    <InfoOutlinedIcon sx={{ fontSize: 18, color: "text.secondary", cursor: "pointer" }} />
                                                </Tooltip>
                                            </Stack>
                                        </TableCell>
                                        <TableCell sx={{ fontWeight: 700, fontSize: "1rem" }}>{t("Value")}</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {cnameRecords.length > 0 ?
                  cnameRecords.map((record, index) => {
                    const relativeHost = getRelativeHost(record.host ?? "", domain);
                    return (
                      <TableRow key={index}>
                                                    <TableCell>
                                                        <Typography fontSize="0.95rem" fontWeight={600} color="text.secondary">
                                                            {t("CNAME")}
                                                        </Typography>
                                                    </TableCell>
                                                    <TableCell>
                                                        <Stack direction="row" alignItems="flex-end" spacing={1} flexWrap="wrap">
                                                            <Stack spacing={0.25}>
                                                                <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ lineHeight: 1.2 }}>
                                                                    {t("Prefix")}
                                                                </Typography>
                                                                <Stack direction="row" alignItems="center" spacing={0.25}>
                                                                    <Typography fontFamily="monospace" fontSize="0.95rem" fontWeight={700}>
                                                                        {relativeHost}
                                                                    </Typography>
                                                                    <CopyAdornment value={relativeHost} />
                                                                </Stack>
                                                            </Stack>
                                                            <Typography fontFamily="monospace" fontSize="0.95rem" color="text.disabled" mb={0.4}>
                                                                .
                                                            </Typography>
                                                            <Stack spacing={0.25}>
                                                                <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ lineHeight: 1.2 }}>
                                                                    {t("Domain")}
                                                                </Typography>
                                                                <Stack direction="row" alignItems="center" spacing={0.25}>
                                                                    <Typography fontFamily="monospace" fontSize="0.95rem">
                                                                        {domain}
                                                                    </Typography>
                                                                    <CopyAdornment value={domain} />
                                                                </Stack>
                                                            </Stack>
                                                        </Stack>
                                                    </TableCell>
                                                    <TableCell>
                                                        <Stack direction="row" alignItems="center" spacing={0.5}>
                                                            <Typography fontFamily="monospace" fontSize="0.95rem" sx={{ wordBreak: "break-all" }}>
                                                                {record.value}
                                                            </Typography>
                                                            <CopyAdornment value={record.value ?? ""} />
                                                        </Stack>
                                                    </TableCell>
                                                </TableRow>);

                  }) :

                  [0, 1, 2].map((i) =>
                  <TableRow key={i}>
                                                <TableCell>
                                                    <Typography fontSize="0.95rem" fontWeight={600} color="text.secondary">{t("CNAME")}</Typography>
                                                </TableCell>
                                                <TableCell />
                                                <TableCell />
                                            </TableRow>
                  )
                  }
                                </TableBody>
                            </Table>

                            <Typography variant="body1" color="text.secondary">
                                {t("Please send these 3 records to your IT department or login to your domain provider (e.g., GoDaddy, Cloudflare) and add them as CNAME records.")}
                                {setupState !== "verified" &&
                <> {t("After you set the records in your nameserver, click on verify.")}</>
                }
                            </Typography>

                            <Stack direction="row" spacing={2} alignItems="center">
                                {setupState !== "verified" &&
                <LoadingButton
                  variant="outlined"
                  loading={setupState === "verifying"}
                  onClick={handleVerify}
                  disabled={!domainId || readOnly}
                  size="large"
                  sx={{ fontSize: "1rem", px: 4 }}>

                                        {setupState === "verifying" ? t("Verifying...") : t("Verify")}
                                    </LoadingButton>
                }

                                {showEdit &&
                <LoadingButton
                  variant="outlined"
                  onClick={handleEdit}
                  disabled={setupState === "verifying" || readOnly}
                  size="large"
                  sx={{ fontSize: "1rem", px: 4 }}>

                                        {t("Edit domain")}
                                    </LoadingButton>
                }
                            </Stack>
                        </Stack>
            }
                </Stack>
            </DialogContent>
        </Dialog>

        <Dialog open={updateTemplatesConfirmOpen} maxWidth="sm" fullWidth onClose={() => !updatingTemplates && setUpdateTemplatesConfirmOpen(false)}>
            <DialogTitle>
                <Typography variant="h6" fontWeight={600}>
                    {t('Update email templates?')}
                </Typography>
            </DialogTitle>
            <DialogContent>
                <DialogContentText>
                    {t('You connected a new sending domain ({{domain}}). Would you like to update the sender address in all your email templates to use this new domain?', { domain: newDomainSuffix })}
                </DialogContentText>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2 }}>
                <Button
            onClick={() => setUpdateTemplatesConfirmOpen(false)}
            disabled={updatingTemplates}>

                    {t('No, keep existing')}
                </Button>
                <LoadingButton
            variant="contained"
            loading={updatingTemplates}
            onClick={handleUpdateTemplates}>

                    {t('Yes, update all')}
                </LoadingButton>
            </DialogActions>
        </Dialog>
        </>);

};

export default ConnectCustomDomainDialog;
